import { Collapse, Descriptions, Drawer, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import React from "react";

const { Panel } = Collapse;

const EvaluationScoreDrawer = ({ visible, data, onClose, onSubmit }: any) => {
  const { supplierName, sectionName, scoreDetails, sectionScore, maxScore, questions } = data;

  const evaluatorColumns = [
    {
      title: "Evaluated By",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Score",
      dataIndex: "ScoreGiven",
      key: "ScoreGiven",
      render: (text: any, record: any) => (
        <span style={{ fontWeight: 600 }}>
          {text}/{record.maxScore || 20}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text: any) => (
        <span>{text && dayjs(text).format("DD-MMM-YYYY")}</span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text: string) => (
        <Tooltip title={text}>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 200,
              display: "inline-block",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
  ];

  const sectionEvaluatorColumns = [
    {
      title: "Evaluated By",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Evaluation Score",
      dataIndex: "ScoreGiven",
      key: "ScoreGiven",
    },
    {
      title: "Date of Evaluation",
      dataIndex: "date",
      key: "date",
      render: (text: any) => (
        <span>{text && dayjs(text).format("DD-MMM-YYYY")}</span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
    },
  ];

  const getAvgScore = (users: any[]) => {
    if (!users || users.length === 0) return 0;
    const total = users.reduce((sum: number, u: any) => sum + u.ScoreGiven, 0);
    return (total / users.length).toFixed(1);
  };

  const getTypeTag = (type: string) => {
    const colorMap: Record<string, string> = {
      "Yes/No": "blue",
      "Single Choice": "green",
      "Multiple Choice": "purple",
      "Short Text": "orange",
      "Long Text": "cyan",
      "File Upload": "magenta",
    };
    return <Tag color={colorMap[type] || "default"}>{type}</Tag>;
  };

  return (
    <Drawer
      title="Evaluation Score"
      width={850}
      onClose={onClose}
      open={visible}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <div
        className="rfqoverview"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Descriptions column={3} style={{ flex: 1, marginLeft: "10px" }}>
          <Descriptions.Item label="Section">
            <span style={{ fontWeight: 600 }}>{sectionName}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Supplier">
            <span style={{ fontWeight: 600 }}>{supplierName}</span>
          </Descriptions.Item>
          {sectionScore !== undefined && (
            <Descriptions.Item label="Section Score">
              <span style={{ fontWeight: 600, color: "#1890ff" }}>
                {sectionScore}/{maxScore}
              </span>
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>

      {/* Section-level evaluator scores */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            marginBottom: 8,
            color: "#333",
          }}
        >
          Section-Level Evaluator Scores
        </div>
        <Table
          dataSource={scoreDetails}
          columns={sectionEvaluatorColumns}
          pagination={false}
          scroll={{ x: "max-content" }}
          size="small"
          bordered
          rowKey="userName"
        />
      </div>

      {/* Question-wise breakdown */}
      {questions && questions.length > 0 && (
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              marginBottom: 8,
              color: "#333",
            }}
          >
            Question-Wise Score Breakdown
          </div>
          <Collapse accordion>
            {questions.map((q: any, idx: number) => {
              const avgScore = getAvgScore(q.users);
              return (
                <Panel
                  key={q.questionId || idx}
                  header={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flex: 1,
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>
                          Q{idx + 1}. {q.question}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginLeft: 12,
                          flexShrink: 0,
                        }}
                      >
                        {getTypeTag(q.type)}
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#1890ff",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Avg: {avgScore}/{q.maxScore || 20}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 16,
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <span style={{ color: "#888", fontSize: 13 }}>
                          Supplier Answer:{" "}
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {q.supplierAnswer || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: "#888", fontSize: 13 }}>
                          Weightage:{" "}
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {q.weightage}%
                        </span>
                      </div>
                    </div>
                    <Table
                      dataSource={q.users?.map((u: any) => ({
                        ...u,
                        maxScore: q.maxScore || 20,
                      }))}
                      columns={evaluatorColumns}
                      pagination={false}
                      size="small"
                      bordered
                      rowKey="userName"
                    />
                  </div>
                </Panel>
              );
            })}
          </Collapse>
        </div>
      )}
    </Drawer>
  );
};

export default EvaluationScoreDrawer;
